import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, Circle, Clock, FileText, MessageSquare, 
  Upload, Download, Calendar, DollarSign, AlertCircle
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

function ProjectTracker() {
  const { language } = useLanguage();
  const [projectCode, setProjectCode] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [projectData, setProjectData] = useState(null);

  const content = {
    sq: {
      title: 'Project Tracker',
      subtitle: 'Gjurmo progresin e projektit tënd',
      loginTitle: 'Kyçu për të parë projektin',
      loginPlaceholder: 'Vendos kodin e projektit',
      loginButton: 'Kyçu',
      phases: ['Planifikimi', 'Dizajni', 'Zhvillimi', 'Testimi', 'Publikimi'],
      messages: 'Mesazhe',
      files: 'Files',
      timeline: 'Timeline',
      payment: 'Pagesa',
      sendMessage: 'Dërgo mesazh',
      writeMessage: 'Shkruaj mesazhin...',
      uploadFile: 'Ngarko file',
      downloadFile: 'Shkarko',
      paid: 'Paguar',
      pending: 'Në pritje',
      upcoming: 'Së shpejti'
    },
    en: {
      title: 'Project Tracker',
      subtitle: 'Track your project progress',
      loginTitle: 'Login to view your project',
      loginPlaceholder: 'Enter project code',
      loginButton: 'Login',
      phases: ['Planning', 'Design', 'Development', 'Testing', 'Launch'],
      messages: 'Messages',
      files: 'Files',
      timeline: 'Timeline',
      payment: 'Payment',
      sendMessage: 'Send message',
      writeMessage: 'Write message...',
      uploadFile: 'Upload file',
      downloadFile: 'Download',
      paid: 'Paid',
      pending: 'Pending',
      upcoming: 'Upcoming'
    }
  };

  const t = content[language];

  // Demo data - Replace with Firebase in production
  const demoProjects = {
    'DEMO123': {
      name: 'Deluxe Restaurant Website',
      currentPhase: 2, // 0-4
      progress: 60,
      phases: [
        { name: t.phases[0], status: 'completed', date: '2024-01-15' },
        { name: t.phases[1], status: 'completed', date: '2024-02-01' },
        { name: t.phases[2], status: 'in-progress', date: '2024-02-15' },
        { name: t.phases[3], status: 'upcoming', date: '2024-03-01' },
        { name: t.phases[4], status: 'upcoming', date: '2024-03-15' }
      ],
      messages: [
        { sender: 'Contenta', text: 'Mirëmëngjes! Projekti ka filluar.', date: '2024-01-15 09:00', isClient: false },
        { sender: 'Ju', text: 'Faleminderit! Prisni me padurim.', date: '2024-01-15 09:30', isClient: true }
      ],
      files: [
        { name: 'Design Mockup v1.fig', size: '12.5 MB', date: '2024-02-01', type: 'Figma' },
        { name: 'Brand Guidelines.pdf', size: '2.3 MB', date: '2024-01-20', type: 'PDF' },
        { name: 'Logo Files.zip', size: '8.1 MB', date: '2024-01-18', type: 'ZIP' }
      ],
      payments: [
        { amount: '€400', status: 'paid', date: '2024-01-15', description: 'Initial Deposit' },
        { amount: '€400', status: 'paid', date: '2024-02-01', description: 'Design Approval' },
        { amount: '€400', status: 'pending', date: '2024-03-01', description: 'Final Payment' }
      ],
      timeline: [
        { event: 'Projekti filloi', date: '2024-01-15', type: 'start' },
        { event: 'Dizajni u aprovua', date: '2024-02-01', type: 'milestone' },
        { event: 'Zhvillimi ka filluar', date: '2024-02-15', type: 'milestone' }
      ]
    }
  };

  const handleLogin = () => {
    if (demoProjects[projectCode]) {
      setIsAuthenticated(true);
      setProjectData(demoProjects[projectCode]);
    } else {
      alert(language === 'sq' ? 'Kod i gabuar' : 'Invalid code');
    }
  };

  if (!isAuthenticated) {
    return (
      <>
        <SEO title={t.title} description={t.subtitle} />
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center py-20 px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 border border-gray-200 dark:border-gray-700"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {t.loginTitle}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">{t.subtitle}</p>
            </div>
            
            <div className="space-y-4">
              <input
                type="text"
                value={projectCode}
                onChange={(e) => setProjectCode(e.target.value)}
                placeholder={t.loginPlaceholder}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 dark:bg-gray-700 dark:text-white"
              />
              <button
                onClick={handleLogin}
                className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-8 py-3 rounded-lg font-semibold shadow-lg transform hover:-translate-y-1 transition-all"
              >
                {t.loginButton}
              </button>
              <p className="text-sm text-gray-500 text-center">
                Demo code: <span className="font-mono font-bold">DEMO123</span>
              </p>
            </div>
          </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO title={`${t.title} - ${projectData.name}`} />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-20">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
              {projectData.name}
            </h1>
            <div className="flex items-center gap-4 text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {projectData.currentPhase + 1} / 5 {t.phases[0].toLowerCase()}
              </span>
              <span>{projectData.progress}% {language === 'sq' ? 'kompletuar' : 'complete'}</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg mb-8 border border-gray-200 dark:border-gray-700">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-6">
              <div
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${projectData.progress}%` }}
              />
            </div>
            
            {/* Phase Steps */}
            <div className="flex justify-between">
              {projectData.phases.map((phase, idx) => (
                <div key={idx} className="flex flex-col items-center">
                  {phase.status === 'completed' ? (
                    <CheckCircle className="w-8 h-8 text-green-500 mb-2" />
                  ) : phase.status === 'in-progress' ? (
                    <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mb-2" />
                  ) : (
                    <Circle className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
                  )}
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300 text-center">
                    {phase.name}
                  </span>
                  <span className="text-xs text-gray-500">{phase.date}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Messages */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <MessageSquare className="w-6 h-6 text-yellow-600" />
                    {t.messages}
                  </h2>
                </div>
                <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
                  {projectData.messages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.isClient ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-xs px-4 py-3 rounded-lg ${msg.isClient ? 'bg-yellow-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'}`}>
                        <p className="text-sm font-semibold mb-1">{msg.sender}</p>
                        <p>{msg.text}</p>
                        <p className="text-xs opacity-75 mt-1">{msg.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder={t.writeMessage}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                    <button className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-2 rounded-lg font-semibold">
                      {t.sendMessage}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Files */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-yellow-600" />
                  {t.files}
                </h3>
                <div className="space-y-3">
                  {projectData.files.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">{file.size}</p>
                      </div>
                      <button className="text-yellow-600 hover:text-yellow-700 ml-2">
                        <Download className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  <button className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-gray-600 dark:text-gray-400 hover:border-yellow-500 hover:text-yellow-600 transition-all flex items-center justify-center gap-2">
                    <Upload className="w-5 h-5" />
                    {t.uploadFile}
                  </button>
                </div>
              </div>

              {/* Payments */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-yellow-600" />
                  {t.payment}
                </h3>
                <div className="space-y-3">
                  {projectData.payments.map((payment, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border-l-4 border-yellow-500 bg-gray-50 dark:bg-gray-700 rounded">
                      <div>
                        <p className="font-bold text-gray-900 dark:text-white">{payment.amount}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{payment.description}</p>
                        <p className="text-xs text-gray-500">{payment.date}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        payment.status === 'paid' 
                          ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                      }`}>
                        {payment.status === 'paid' ? t.paid : t.pending}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Timeline */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-yellow-600" />
                  {t.timeline}
                </h3>
                <div className="space-y-4">
                  {projectData.timeline.map((event, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-yellow-500 rounded-full mt-2" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {event.event}
                        </p>
                        <p className="text-xs text-gray-500">{event.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ProjectTracker;